import {
  TotalOpportunities,
  OpportunityComplete,
  CaseInfo,
  Result,
  OpportunitySearchResult,
  OpportunityAssignDto,
  OpportunityEditContactDto,
  SalesChannel,
  Slot,
  EditContact,
  StartCallResponseBody,
  CallBodyRequest,
} from '../dto/model';

const rootUrl = (process as any).env.REACT_APP_API_ROOT_URL;

/** Parses API responses that use the HttpResult { data, isSuccess, errors } shape. */
const parseResultResponse = async (response: Response): Promise<Result<any>> => {
  const text = await response.text();
  if (!text.trim()) {
    if (response.ok) {
      return { isSuccess: true, data: {}, errors: [] };
    }
    const statusMessage =
      response.status === 403
        ? 'Access denied. Your account may not have permission to assign inbound cases.'
        : `HTTP ${response.status}: ${response.statusText}`;
    return { isSuccess: false, errors: [statusMessage], data: {} };
  }
  try {
    return JSON.parse(text);
  } catch {
    return {
      isSuccess: false,
      errors: [text.length > 300 ? `HTTP ${response.status}: ${response.statusText}` : text],
      data: {},
    };
  }
};

const handleResponse = async (url: string, options: RequestInit): Promise<Response> => {
  const response = await fetch(url, options);

  if (!response.ok) {
    throw {
      status: response.status,
      statusText: response.statusText,
    };
  }

  return response;
};

export const bookingPageService = {
  async startCallAsync(oppId: string, body: CallBodyRequest, accessToken: string): Promise<StartCallResponseBody> {
    const url = `${rootUrl}/api/v1/opportunities/${oppId}/calls`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(body),
      });
      const result = await response.json();
      return result;
    } catch (e: any) {
      const error = new Error('Failed to start call');
      (error as any).status = 500;
      (error as any).statusText = 'Failed to start call';
      throw error;
    }
  },

  async updateContact(editContactDto: OpportunityEditContactDto, salesChannel: SalesChannel, accessToken: string): Promise<Result<any>> {
    const url = `${rootUrl}/api/v1/case/contact?salesChannel=${salesChannel}`;

    try {
      const response = await handleResponse(url, {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }),
        body: JSON.stringify(editContactDto),
      });
      const result = await response.json();
      return result;
    } catch (e: any) {
      return {
        isSuccess: false,
        errors: ['couldnt update contact'],
        data: {},
      };
    }
  },

  async cancelAppointment(externalAppointmentId: string, opportunityId: string, accessToken: string, isRescheudling: boolean): Promise<Result<Slot>> {
    const url = `${rootUrl}/api/v1/slots/${externalAppointmentId}/cancel?opportunityId=${opportunityId}&isRescheduling=${isRescheudling}`;
    const response = await handleResponse(url, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const result = await response.json();
    return result;
  },

  async searchForOpportunities(accessToken: string, query: string): Promise<OpportunitySearchResult[]> {
    const url = `${rootUrl}/api/v1/search?${query}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      const result = await response.json();
      return result;
    } catch (e: any) {
      return [];
    }
  },

  async assignInboundOpportunity(body: OpportunityAssignDto, accessToken: string): Promise<Result<any>> {
    const url = `${rootUrl}/api/v1/inbound-center/assign-case`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }),
        body: JSON.stringify(body),
      });
      return await parseResultResponse(response);
    } catch (e: any) {
      return {
        isSuccess: false,
        errors: [e?.message ?? 'Failed to assign case'],
        data: {},
      };
    }
  },

  async getOrAssignPriorityCase(accessToken: string, salesChannel: string): Promise<Result<CaseInfo>> {
    let url = `${rootUrl}/api/v1/case?salesChannel=${salesChannel}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      const result = await response.json();
      return result;
    } catch (e: any) {
      return {
        data: {} as CaseInfo,
        isSuccess: false,
        errors: [e?.statusText],
      };
    }
  },

  async getAssignedCase(accessToken: string, salesChannel: string): Promise<Result<CaseInfo>> {
    let url = `${rootUrl}/api/v1/assigned-case?salesChannel=${salesChannel}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
        }),
      });

      return await parseResultResponse(response);
    } catch (e: any) {
      return {
        data: {} as CaseInfo,
        isSuccess: false,
        errors: [e?.statusText ?? e?.message ?? 'Failed to load assigned case'],
      };
    }
  },

  async closeCase(caseComplete: OpportunityComplete, salesChannel: string, accessToken: string): Promise<Result<CaseInfo>> {
    let url = `${rootUrl}/api/v1/case?salesChannel=${salesChannel}`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(caseComplete),
      });

      if (!response.ok) {
        return {
          isSuccess: false,
          errors: [`HTTP ${response.status}: ${response.statusText}`],
          data: {} as CaseInfo,
        };
      }

      const result = await response.json();
      return result;
    } catch (e: any) {
      return {
        isSuccess: false,
        errors: [e?.message || 'Failed to close case'],
        data: {} as CaseInfo,
      };
    }
  },

  async releaseCase(id: string, salesChannel: string, accessToken: string): Promise<void> {
    let url = `${rootUrl}/api/v1/case/release?id=${id}&salesChannel=${salesChannel}`;
    const response = await handleResponse(url, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
  },

  async addCaseComment(internalOppId: string, comment: string, salesChannel: string, accessToken: string): Promise<void> {
    try
    {
      let url = `${rootUrl}/api/v1/case/comment?salesChannel=${salesChannel}`;
      const body = {
        id: internalOppId,
        comment,
      };
      const response = await handleResponse(url, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(body),
      });
      const result = await response.json();
    } catch {}
  },

  async updateCaseAssignment(internalId: string, salesChannel: string, accessToken: string): Promise<Result<Date>> {
    let url = `${rootUrl}/api/v1/case-assignment?internalId=${internalId}&salesChannel=${salesChannel}`;
    const response = await handleResponse(url, {
      method: 'PATCH',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
    });
    const result = await response.json();
    return result;
  },

  async getAllCases(accessToken: string, shouldLimit: boolean): Promise<TotalOpportunities> {
    const url = `${rootUrl}/api/v1/cases?shouldLimit=${shouldLimit}`;
    const response = await handleResponse(url, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
    });

    const result = await response.json();
    return result;
  },

  /**
   * Downloads all cases as CSV from the export endpoint (controller-style file response).
   */
  async downloadAllCasesAsCsv(
    accessToken: string,
    filename: string = 'opportunity_queue_bookingos',
  ): Promise<void> {
    const url = `${rootUrl}/api/v1/cases/export`;
    const response = await handleResponse(url, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  async BulkUploadOpportunities(req: any, salesChannel: string, accessToken: string) {
    const url = `${rootUrl}/api/v1/cases/bulk-upload?salesChannel=${salesChannel}`;
    const response = await handleResponse(url, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(req),
    });
    return await response.text();
  },

  async getContactsToEdit(accessToken: string, isCompleted?: boolean | null): Promise<Result<EditContact[]>> {
    const isCompletedParam = isCompleted !== null && isCompleted !== undefined ? `?isCompleted=${isCompleted}` : '';
    const url = `${rootUrl}/api/v1/case/contact/edit${isCompletedParam}`;
    const response = await handleResponse(url, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const result = await response.json();
    return result;
  },

  async postEditContacts(editContacts: EditContact[], accessToken: string): Promise<Result<any>> {
    const url = `${rootUrl}/api/v1/case/contact/edit`;
    const response = await handleResponse(url, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(editContacts),
    });
    const result = await response.json();
    return result;
  },
};
